import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuthStore } from '@/stores/authStore';
import { useToast } from '@/hooks/use-toast';
import { WealthicaStorage } from '../utils/storage';
import { WealthicaError, WEALTHICA_ERROR_CODES } from '../types/errors';
import { useMultiWealthica2Store } from '@/stores/multiWealthica2Store';
import { logger } from '@/services/LoggingService';
import { clearCallbackDedupFlags } from '../utils/navigation-flags';
import { dismissFlowToast } from '../utils/flowToast';

interface DisconnectParams {
  institutionId: string;
  userId?: string;
  forceLocal?: boolean; // NEW: Force local-only operation
}

/**
 * Enhanced React Query hook for disconnecting from Wealthica institutions
 * Supports both token-based and token-free operations
 * Respects user's data persistence preferences
 */
export const useWealthicaDisconnect = () => {
  const { user } = useAuthStore();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Multi-store only
  const multiStore = useMultiWealthica2Store();
  const persistDataPreference = multiStore.persistDataPreference;

  return useMutation({
    mutationFn: async ({ institutionId, userId, forceLocal = false }: DisconnectParams) => {
      const operationId = crypto.randomUUID(); // 🔥 NEW: Operation tracking
      const targetUserId = userId || user?.id;
      
      if (!targetUserId) {
        throw new WealthicaError(
          'User ID is required for disconnect operation',
          WEALTHICA_ERROR_CODES.AUTH_FAILED,
          false
        );
      }

      logger.info('useWealthicaDisconnect', 'Starting disconnect operation', {
        operationId,
        institutionId,
        userId: targetUserId.substring(0, 8) + '...', // 🔥 NEW: Privacy-safe logging
        persistDataPreference,
        forceLocal
      });

      // Proactively dismiss any existing connect flow banner to avoid overlap
      try { dismissFlowToast('wealthica-flow'); } catch { /* noop */ }

      // Local operations independence: perform local-mode disconnect unconditionally
      const useLocalMode = true;
      
      // Disconnect button should ALWAYS trigger nuclear delete
      // Users expect disconnect to remove all data regardless of persistence preference
      // Persistence preference only affects automatic saving, not disconnect behavior
      const deleteMode = 'full_delete';

      // Server-only tokens: do not compute token state from storage
      const tokenState = 'server_only_tokens';

      logger.info('useWealthicaDisconnect', 'Disconnect configuration', {
        operationId,
        tokenState,
        useLocalMode,
        deleteMode,
        persistDataPreference
      });

      // Derive provider name from multi-store connection (fallback to 'wealthica')
      const connection = multiStore.getConnection(institutionId);
      const providerName = connection?.providerName || 'wealthica';

      // Database operations (align payload with Edge Function contract)
      const { data, error } = await supabase.functions.invoke('wealthica2-delete-accounts', {
        body: {
          user_id: targetUserId,
          institution_id: institutionId,
          provider_name: providerName,
          // Provide stable identifier to help backend resolve duplicates
          institution_identifier: connection?.connectionData?.institutionIdentifier,
          preserve_connection: false, // explicit for disconnect
          delete_mode: deleteMode,
          operation_mode: useLocalMode ? 'local' : 'api'
        }
      });

      if (error) {
        logger.error('useWealthicaDisconnect', 'Database operation failed', { 
          operationId, 
          error 
        });
        throw new WealthicaError(
          error.message || 'Failed to disconnect from institution',
          WEALTHICA_ERROR_CODES.API_ERROR,
          true
        );
      }

      // Cache management - always full delete for disconnect
      await WealthicaStorage.clearInstitutionCache(institutionId);
      logger.info('useWealthicaDisconnect', 'Disconnect: cleared institution cache', { operationId, institutionId });
      
      logger.info('useWealthicaDisconnect', 'Database operation completed', { operationId });

      // Handle global cache - respect user's persistence preference
      // CRITICAL: Clear tokens immediately to prevent auto-reconnection
      logger.info('useWealthicaDisconnect', 'Clearing tokens to prevent auto-reconnection', { operationId, persistDataPreference });
      
      if (persistDataPreference) {
        // Only clear auth tokens, preserve cached data for "Disconnected" state
        WealthicaStorage.clearConnectionDataOnly();
        logger.info('useWealthicaDisconnect', 'Preserved cached data due to persistence preference', { operationId });
      } else {
        // Nuclear clear - remove EVERYTHING for clean slate when persistence is disabled
        WealthicaStorage.clearEverything();
        logger.info('useWealthicaDisconnect', 'Nuclear clear completed - all data removed for clean slate', { operationId });
      }

      // Also clear callback de-dup flags to avoid blocking the next connect
      try {
        clearCallbackDedupFlags();
      } catch (e) {
        logger.warn('useWealthicaDisconnect', 'Failed to clear callback dedup flags', {
          operationId,
          error: e instanceof Error ? e.message : String(e)
        });
      }
      
      if (data?.remainingConnections === 0) {
        logger.info('useWealthicaDisconnect', 'Last institution disconnected', { operationId });
      }

      logger.info('useWealthicaDisconnect', 'Disconnect completed successfully', {
        operationId,
        institutionId,
        deleteMode,
        remainingConnections: data?.remainingConnections || 0
      });

      return { ...data, operationId };
    },

    onSuccess: async (data, { institutionId, forceLocal }) => {
      // Coordinate disconnect operations to prevent race conditions
      const operationId = data?.operationId || 'unknown';
      
      try {
        // Step 1: Clear store state - respect persistence preference
        // Multi-store mode - remove specific connection
        multiStore.removeConnection(institutionId);
        logger.info('useWealthicaDisconnect.onSuccess', 'Multi-store connection removed', { 
          operationId, 
          institutionId,
          preservedCache: persistDataPreference 
        });
        
        // Step 2: Handle React Query cache coordination
        const remainingConnections = data?.remainingConnections ?? 0;
        
        logger.info('useWealthicaDisconnect.onSuccess', 'Coordinating query cache updates', {
          institutionId,
          remainingConnections,
          operationId,
          strategy: remainingConnections > 0 ? 'invalidate' : 'reset'
        });

        // Batch cache operations to prevent race conditions
        await Promise.all([
          remainingConnections > 0 
            ? Promise.all([
                queryClient.invalidateQueries({ queryKey: ['wealthica-connections'] }),
                queryClient.invalidateQueries({ queryKey: ['wealthica2', 'accounts'] }),
                queryClient.invalidateQueries({ queryKey: ['multiWealthica2', 'accounts'] }),
                queryClient.invalidateQueries({ queryKey: ['investment-holdings'] })
              ])
            : Promise.all([
                queryClient.resetQueries({ queryKey: ['wealthica-connections'] }),
                queryClient.resetQueries({ queryKey: ['wealthica2', 'accounts'] }),
                queryClient.resetQueries({ queryKey: ['multiWealthica2', 'accounts'] }),
                queryClient.resetQueries({ queryKey: ['investment-holdings'] })
              ])
        ]);
        
        logger.info('useWealthicaDisconnect.onSuccess', 'All cache operations completed', { operationId });

        // Terminal toast only
        toast({
          title: "Disconnected Successfully",
          description: "All account data has been removed.",
          variant: "default"
        });
        
      } catch (cacheError) {
        logger.error('useWealthicaDisconnect.onSuccess', 'Cache coordination error', { 
          operationId, 
          error: cacheError 
        });
        // Still show success since database operation succeeded.
        toast({
          title: "Disconnected Successfully", 
          description: "Account disconnected (cache cleanup may be delayed).",
          variant: "default"
        });
      }
    },

    onError: (error: Error, variables?: DisconnectParams) => {
      logger.error('useWealthicaDisconnect', 'Operation failed', { error });
      toast({
        title: "Disconnect Failed",
        description: "Unable to disconnect. Please try again or contact support.",
        variant: "destructive"
      });
    }
  });
};
